<!--
  变更日志：页头 + 筛选卡 + 表格卡 + 分页
  数据全部来自「变更日志列表」接口，请求 / 响应字段名与接口一致：
    请求 params：uf / startTimeIso / endTimeIso / cnpj / ie / triggerType
    响应 items：ts / time / subjectId / uf / cnpj / ie / changeField / beforeValue / afterValue / triggerType / operatorId / operatorName
-->
<template>
  <div class="change-log">
    <!-- 页头 -->
    <div class="page-header">
      <div>
        <h2 style="margin-top: 0;">
          状态变更日志
          <span class="h2-pt">Log de Alterações de Status</span>
        </h2>
        <div class="desc">
          <span>所有 IE 状态变更的留痕记录，可按时间、UF、触发方式筛选</span>
          <span class="desc-pt">
            Registro de todas as alterações de status de IE; filtre por período, UF e forma de gatilho
          </span>
        </div>
      </div>
      <div class="right">
        <el-button size="small" @click="handleExport">
          <bi-label zh="导出" pt="Exportar" inline />
        </el-button>
      </div>
    </div>

    <!-- 筛选 -->
    <el-card class="filter-card" shadow="never">
      <el-form
        :model="searchParam" label-position="top"
        size="small"
      >
        <el-row :gutter="16">
          <!-- CNPJ / IE：label 处下拉切换字段（与 IE 信息管理一致），默认 CNPJ -->
          <el-col :span="6">
            <el-form-item class="kw-search">
              <template slot="label">
                <el-select v-model="searchParam.params.kwType">
                  <el-option
                    v-for="o in kwTypeOptions" :key="o.value"
                    :label="o.label + (o.labelPt ? ' / ' + o.labelPt : '')"
                    :value="o.value"
                  />
                </el-select>
              </template>
              <el-input
                v-model="searchParam.params.kw"
                type="textarea"
                :autosize="{ minRows: 4, maxRows: 4 }"
                :placeholder="$t('GlobalSetObj.pleaseInput')"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="变更时间" pt="Data da Alteração" inline />
              </template>
              <el-date-picker
                v-model="searchParam.params.time"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="~"
                start-placeholder="开始日期 / Início"
                end-placeholder="结束日期 / Fim"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="UF" inline />
              </template>
              <el-select
                v-model="searchParam.params.uf"
                placeholder="全部 / Todos"
                style="width: 100%"
                multiple
                collapse-tags
                clearable
                filterable
              >
                <el-option
                  v-for="uf in ufOptions" :key="uf.id" :label="uf.label"
                  :value="uf.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item>
              <template slot="label">
                <bi-label zh="触发方式" pt="Forma de Gatilho" inline />
              </template>
              <el-select
                v-model="searchParam.params.triggerType" clearable placeholder="全部 / Todos"
                style="width: 100%"
              >
                <el-option
                  v-for="o in triggerOptions" :key="o.value" :label="o.label + ' / ' + o.labelPt"
                  :value="o.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item class="filter-btns">
              <template slot="label">
                <span class="ph-label">&nbsp;</span>
              </template>
              <el-button size="small" type="primary" @click="applyFilter">
                <bi-label zh="查询" pt="Consultar" inline />
              </el-button>
              <el-button size="small" @click="resetFilter">
                <bi-label zh="重置" pt="Redefinir" inline />
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="tableLoading" :data="pagedRows"
        border
        size="mini"
        :row-class-name="rowCls"
        style="width: 100%"
      >
        <el-table-column min-width="170" class-name="cell-num">
          <template slot="header">
            <bi-label zh="变更时间" pt="Data da Alteração" />
          </template>
          <template slot-scope="{ row }">{{ fmtLogTime(row) }}</template>
        </el-table-column>
        <el-table-column min-width="80">
          <template slot="header">
            <bi-label zh="UF" />
          </template>
          <template slot-scope="{ row }"><strong>{{ row.uf }}</strong></template>
        </el-table-column>
        <el-table-column min-width="170" class-name="cell-num">
          <template slot="header">
            <bi-label zh="CNPJ" />
          </template>
          <template slot-scope="{ row }">{{ fmtCnpj(row.cnpj) }}</template>
        </el-table-column>
        <el-table-column min-width="150" class-name="cell-num">
          <template slot="header">
            <bi-label zh="IE" />
          </template>
          <template slot-scope="{ row }">{{ row.ie }}</template>
        </el-table-column>
        <el-table-column min-width="150" show-overflow-tooltip>
          <template slot="header">
            <bi-label zh="变更字段" pt="Campo Alterado" />
          </template>
          <template slot-scope="{ row }">
            <div v-for="field in row.changeFields" :key="field">
              {{ field }}
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="140" show-overflow-tooltip>
          <template slot="header">
            <bi-label zh="变更前" pt="Valor Anterior" />
          </template>
          <template slot-scope="{ row }">
            <div v-for="before in row.beforeValues" :key="before">{{ before }}</div>
          </template>
        </el-table-column>
        <el-table-column min-width="140" show-overflow-tooltip>
          <template slot="header">
            <bi-label zh="变更后" pt="Novo Valor" />
          </template>
          <template slot-scope="{ row }">
            <div v-for="after in row.afterValues" :key="after">{{ after }}</div>
          </template>
        </el-table-column>
        <el-table-column min-width="180">
          <template slot="header">
            <bi-label zh="触发方式" pt="Forma de Gatilho" />
          </template>
          <template slot-scope="{ row }">
            <span class="tag" :class="triggerCls(row.triggerType)">
              <span class="dot" />{{ triggerZh(row.triggerType) }}
              <span class="tag-pt">/ {{ triggerPt(row.triggerType) }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column min-width="140">
          <template slot="header">
            <bi-label zh="操作人" pt="Operador" />
          </template>
          <template slot-scope="{ row }">{{ operatorText(row) }}</template>
        </el-table-column>
      </el-table>

      <!-- 分页：共 N 条 + 每页条数 + 翻页 + 前往页 -->
      <div class="pagination">
        <el-pagination
          :current-page.sync="searchParam.pageNumber"
          :page-size.sync="searchParam.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="slot, sizes, prev, pager, next, jumper"
          class="cl-pagination"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        >
          <span class="pb-total">
            共 {{ total }} 条<span class="pb-pt">/ Total: {{ total }} registros</span>
          </span>
        </el-pagination>
      </div>
    </el-card>

  </div>
</template>

<script>
import BiLabel from './BiLabel';
import {
  CHECK_SOURCE_LABEL,
  todayStr, fmtCnpj, cnpjDigits
} from '../mock';
import { getInvoiceSubjectStatusMonitorLogExport, getInvoiceSubjectStatusMonitorLogList } from '@/api/invoiceCenter';
import { goResourceTaskList } from '@/utils/goExportList';

export default {
  name: 'ChangeLog',
  components: { BiLabel },
  props: {
    stateList: { type: Array, default: () => [] }
  },
  data() {
    return {
      // 查询参数结构同 IE 信息管理：pageSize / pageNumber / params（params 键名与接口一致）
      searchParam: {
        pageSize: 10,
        pageNumber: 1,
        params: {
          kwType: 'cnpj',
          kw: '',
          // 时间区间默认当天
          time: [todayStr(), todayStr()],
          uf: [],
          triggerType: null
        }
      },
      total: 0,
      tableLoading: false,
      pagedRows: [],
      // 关键词字段（CNPJ / IE 二选一，对应接口 params.cnpj / params.ie）
      kwTypeOptions: [
        { value: 'cnpj', label: 'CNPJ', labelPt: '' },
        { value: 'ie', label: 'IE', labelPt: '' }
      ]
    };
  },
  computed: {
    /** UF 下拉选项：巴西 27 州 UF（静态字典） */
    ufOptions() {
      return this.stateList;
    },
    /** 触发方式选项：triggerType 1定时任务 2手动触发 */
    triggerOptions() {
      return Object.keys(CHECK_SOURCE_LABEL).map(k => ({
        value: Number(k),
        label: CHECK_SOURCE_LABEL[k].zh,
        labelPt: CHECK_SOURCE_LABEL[k].pt
      }));
    }
  },
  mounted() {
    this.getTableData();
  },
  methods: {
    // ============ 接口 ============
    /**
     * 统一封装接口调用：失败只打日志并返回 null，由调用方提示
     * @param {string} label 接口名称（仅用于日志）
     * @param {Function} fn 真正的请求函数
     * @returns {Promise<object|null>} 成功返回 res，失败返回 null
     */
    async callApi(label, fn) {
      try {
        return await fn();
      } catch (e) {
        console.error(`[变更日志] ${label} 请求失败`, e);
        return null;
      }
    },
    /** 拉取变更日志：请求失败清空列表（空态）并提示，不做本地兜底 */
    async getTableData() {
      this.tableLoading = true;
      const param = {
        pageNumber: this.searchParam.pageNumber,
        pageSize: this.searchParam.pageSize,
        params: this.queryConditions()
      };
      const res = await this.callApi('page', () => getInvoiceSubjectStatusMonitorLogList(param));
      if (res && res.code === 200) {
        const data = res.data || {};
        const list = Array.isArray(data.items) ? data.items : [];
        this.pagedRows = list;
        // 接口 total 可能为 null（按游标翻页），此时用已加载条数兜底展示
        this.total = typeof data.total === 'number'
          ? data.total
          : (this.searchParam.pageNumber - 1) * this.searchParam.pageSize + list.length;
      } else {
        this.pagedRows = [];
        this.total = 0;
        this.$message.error('变更日志接口请求失败，请稍后重试 / Falha ao consultar o log de alterações');
      }
      this.tableLoading = false;
    },

    // ============ 筛选 / 分页 ============
    /** 整理查询参数：字段名与接口一致，未填的条件置为 null */
    queryConditions() {
      const { time, uf, triggerType, kw, kwType } = this.searchParam.params;
      const [start, end] = Array.isArray(time) ? time : [null, null];
      return {
        uf: uf || [],
        // 时间区间转 ISO（巴西时区 -03:00）：开始日 00:00:00，结束日 23:59:59
        startTimeIso: this.toIso(start, false),
        endTimeIso: this.toIso(end, true),
        cnpj: kwType === 'cnpj' ? this.getKeywords(kw) : [],
        ie: kwType === 'ie' ? this.getKeywords(kw) : [],
        triggerType: triggerType || null
      };
    },
    /** 日期 → ISO 字符串（yyyy-MM-ddTHH:mm:ss-03:00）；空值返回 null */
    toIso(day, isEnd) {
      if (!day) return null;
      return `${day}T${isEnd ? '23:59:59' : '00:00:00'}-03:00`;
    },
    /** 关键词按回车拆分并去重（最多 2000 个）；CNPJ 需去掉符号后再查询 */
    getKeywords(value) {
      if (!value || !String(value).length) return [];
      const strip = this.searchParam.params.kwType === 'cnpj';
      const arr = String(value)
        .split(/\r?\n/)
        .map(item => (strip ? cnpjDigits(item) : item.replace(/\s+/g, '')))
        .filter(Boolean);
      return [...new Set(arr)].slice(0, 2000);
    },
    /** 翻页：接口分页，切页时重新查询（不再本地切片） */
    handlePageChange(page) {
      this.searchParam.pageNumber = page;
      this.getTableData();
    },
    /** 切换每页条数：回到第 1 页重新查询 */
    handleSizeChange(size) {
      this.searchParam.pageSize = size;
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 点击查询：回到第 1 页重新请求 */
    applyFilter() {
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },
    /** 重置：所有筛选项回到默认值（关键词字段回到 CNPJ、时间回到当天）并回到第 1 页 */
    resetFilter() {
      this.searchParam.params = {
        kwType: 'cnpj', kw: '', time: [todayStr(), todayStr()], uf: [], triggerType: null
      };
      this.searchParam.pageNumber = 1;
      this.getTableData();
    },

    // ============ 单元格渲染 ============
    /** 触发方式中文（1定时任务 2手动触发） */
    triggerZh(v) {
      return (CHECK_SOURCE_LABEL[v] && CHECK_SOURCE_LABEL[v].zh) || '—';
    },
    /** 触发方式葡语 */
    triggerPt(v) {
      return (CHECK_SOURCE_LABEL[v] && CHECK_SOURCE_LABEL[v].pt) || '';
    },
    /** 触发方式标签配色：定时任务蓝 / 手动触发橙 */
    triggerCls(v) {
      return Number(v) === 1 ? 'tag-info' : 'tag-warn';
    },
    /** 操作人：定时任务（无操作人）统一显示「系统 / Sistema」 */
    operatorText(row) {
      return row.operatorName || (row.operatorId ? String(row.operatorId) : '系统');
    },
    /** 整行样式：非状态类字段（有效期限 / 基础信息）淡色高亮，便于区分 */
    rowCls({ row }) {
      return Number(row.changeField) > 3 ? 'row-field' : '';
    },

    // ============ 工具 ============
    /** CNPJ 展示：接口返回的无符号 14 位 → 00.000.000/0000-00 */
    fmtCnpj(value) {
      return fmtCnpj(value);
    },
    /** 日志时间：优先用毫秒时间戳 ts，其次 ISO 时间 time，格式化为 yyyy-MM-dd HH:mm:ss */
    fmtLogTime(row) {
      const src = row.ts ? new Date(Number(row.ts)) : (row.time ? new Date(row.time) : null);
      if (!src || isNaN(src.getTime())) return row.time || '—';
      const p = n => String(n).padStart(2, '0');
      return `${src.getFullYear()}-${p(src.getMonth() + 1)}-${p(src.getDate())} ${p(src.getHours())}:${p(src.getMinutes())}:${p(src.getSeconds())}`;
    },

    // ============ 操作 ============
    /** 导出当前筛选结果（暂无导出接口，只提示条数） */
    handleExport() {
      getInvoiceSubjectStatusMonitorLogExport(this.queryConditions()).then(res => {
        if (res.code === 200) {
          goResourceTaskList();
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.change-log {
  padding: 4px 0 20px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}
.page-header h2 {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.page-header h2 .h2-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}
.page-header .desc {
  color: #86909c;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
}
.page-header .desc .desc-pt {
  font-size: 12px;
}
.page-header .right {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.filter-card {
  margin-bottom: 12px;
  border-radius: 8px;
}
.table-card {
  border-radius: 8px;
}

::v-deep .el-card__body { padding: 20px; }
::v-deep .el-form-item { margin-bottom: 0; }
::v-deep .el-form-item__label { padding-bottom: 4px; line-height: 20px; }
.ph-label { display: block; }
::v-deep .filter-btns .el-form-item__content { display: flex; }
/* 日期区间选择器宽度自适应（默认较宽，会挤掉同一行的其他项） */
::v-deep .el-date-editor--daterange.el-input__inner { width: 100%; }
/* CNPJ / IE：label 处的字段下拉做成「无边框标签下拉」（同 IE 信息管理） */
::v-deep .kw-search .el-form-item__label {
  width: 100%;
  padding-bottom: 2px;
}
::v-deep .kw-search .el-form-item__label .el-select { width: 100%; }
::v-deep .kw-search .el-form-item__label .el-input--small .el-input__inner {
  border: none;
  padding-left: 0;
  height: 26px;
  line-height: 26px;
  font-weight: 600;
  color: #1d2129;
  background: transparent;
}
::v-deep .kw-search .el-form-item__label .el-input__suffix { right: 0; }

::v-deep .cell-num .cell {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 14px;
}
::v-deep .el-table th .cell { line-height: 1.35; }
/* 非状态类字段（有效期限 / 基础信息）淡紫高亮 */
::v-deep .el-table__row.row-field {
  background: #faf9ff;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.tag .tag-pt {
  font-size: 12px;
  font-weight: 400;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-info { background: #e6f0ff; color: #165dff; }
.tag-gray { background: #f2f3f5; color: #4e5969; }
.tag-brand { background: #f0eaff; color: #6b4eff; }

.val-ok { color: #00b42a; font-weight: 600; }
.val-err { color: #f53f3f; font-weight: 600; }
.val-warn { color: #ff7d00; font-weight: 600; }
.val-empty { color: #c9cdd4; }
.val-date {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.val-pt {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}

.empty-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 32px 12px;
  color: #86909c;
  font-size: 14px;
}
.empty-row .empty-pt {
  font-size: 14px;
}

/* 分页：el-pagination（总数走自定义 slot，保留中葡双语） */
.cl-pagination {
  padding: 14px 0 2px;
  font-size: 12px;
  color: #4e5969;
}
.pb-total {
  display: flex;
  flex-direction: column;
  font-weight: 400;
  line-height: 1.35;
}
.pb-total .pb-pt {
  font-size: 12px;
  color: #86909c;
}
::v-deep .cl-pagination .el-pagination__sizes { margin: 0 8px 0 12px; }
::v-deep .cl-pagination .btn-prev,
::v-deep .cl-pagination .btn-next,
::v-deep .cl-pagination .el-pager li,
::v-deep .cl-pagination .el-input__inner {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  line-height: 26px;
  margin: 0 3px;
  font-weight: 400;
}
::v-deep .cl-pagination .el-pager li.active {
  background: #00a551;
  border-color: #00a551;
  color: #fff;
}
::v-deep .cl-pagination .el-pagination__jump { margin-left: 8px; }
</style>
